import {
  Children,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import type { FlipBookHandle } from "./FlipBook";

type Props = {
  children: ReactNode;
  onFlip?: (pageIndex: number) => void;
  onInit?: (pageCount: number) => void;
  /** Slide the carousel opens on (default 0). */
  initialIndex?: number;
};

const SWIPE_DISTANCE_RATIO = 0.2;
const FLICK_VELOCITY_PX_PER_MS = 0.5;
const AXIS_LOCK_PX = 6;

/**
 * Single-page horizontal swipe carousel. Implements the same imperative
 * handle as FlipBook so Reader's buttons / keyboard / counter wiring stay
 * identical between desktop (FlipBook) and mobile (this).
 *
 * page-flip 2.0.7's single-page mode has structural issues — soft pages
 * reuse the front image on the back face, the corner hit-zone is huge,
 * tap-to-flip plays a slide-in-from-left that doesn't look like a flip,
 * and the flip-prev path is gated by disableFlipByClick. Every one of
 * those is a workaround in FlipBook.tsx that only matters on mobile.
 * On a phone the curl animation isn't selling the book metaphor anyway,
 * so we drop the engine here and ship a plain slide.
 */
const MobileCarousel = forwardRef<FlipBookHandle, Props>(function MobileCarousel(
  { children, onFlip, onInit, initialIndex },
  ref,
) {
  const slides = Children.toArray(children).filter(isValidElement);
  const count = slides.length;

  const initial = Math.max(0, Math.min(count - 1, initialIndex ?? 0));
  const [index, setIndex] = useState(initial);
  const indexRef = useRef(initial);
  indexRef.current = index;

  const viewportRef = useRef<HTMLDivElement>(null);

  type Drag = {
    pointerId: number;
    startX: number;
    startY: number;
    startT: number;
    lastX: number;
    lastT: number;
    width: number;
    axis: "h" | "v" | null;
  };
  const dragRef = useRef<Drag | null>(null);
  const [dragPx, setDragPx] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const goTo = useCallback(
    (next: number) => {
      const clamped = Math.max(0, Math.min(count - 1, next));
      if (clamped === indexRef.current) return;
      indexRef.current = clamped;
      setIndex(clamped);
      onFlip?.(clamped);
    },
    [count, onFlip],
  );

  useImperativeHandle(ref, () => ({
    flipNext: () => goTo(indexRef.current + 1),
    flipPrev: () => goTo(indexRef.current - 1),
    getCurrentPageIndex: () => indexRef.current,
    getPageCount: () => count,
  }));

  // Fire once per mount-and-whenever-count-changes; matches FlipBook's
  // onInit contract that Reader uses to drive the page counter total.
  useEffect(() => {
    onInit?.(count);
  }, [count, onInit]);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    const node = viewportRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startT: e.timeStamp,
      lastX: e.clientX,
      lastT: e.timeStamp,
      width: rect.width,
      axis: null,
    };
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (d.axis === null) {
      const ax = Math.abs(dx);
      const ay = Math.abs(dy);
      if (ax < AXIS_LOCK_PX && ay < AXIS_LOCK_PX) return;
      if (ax <= ay) {
        // Vertical or diagonal — let the page scroll, give up the gesture.
        dragRef.current = null;
        return;
      }
      d.axis = "h";
      setIsDragging(true);
      // Capture so we still see move/up if the pointer leaves the viewport
      // (e.g., the user drags onto the topbar).
      try {
        viewportRef.current?.setPointerCapture(e.pointerId);
      } catch {
        /* setPointerCapture is best-effort; ignore unsupported pointers */
      }
    }
    d.lastX = e.clientX;
    d.lastT = e.timeStamp;
    // Rubber-band at the ends so it feels bounded rather than dead.
    let offset = dx;
    if (
      (indexRef.current === 0 && dx > 0) ||
      (indexRef.current === count - 1 && dx < 0)
    ) {
      offset = dx / 3;
    }
    setDragPx(offset);
  };

  const finishDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    dragRef.current = null;
    try {
      viewportRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* mirrors setPointerCapture above */
    }
    if (d.axis !== "h") {
      setDragPx(0);
      setIsDragging(false);
      return;
    }
    const dx = d.lastX - d.startX;
    const dt = Math.max(1, d.lastT - d.startT);
    const velocity = dx / dt;
    const distanceThreshold = d.width * SWIPE_DISTANCE_RATIO;
    let target = indexRef.current;
    if (dx < -distanceThreshold || velocity < -FLICK_VELOCITY_PX_PER_MS) {
      target += 1;
    } else if (dx > distanceThreshold || velocity > FLICK_VELOCITY_PX_PER_MS) {
      target -= 1;
    }
    setDragPx(0);
    setIsDragging(false);
    goTo(target);
  };

  const trackStyle: CSSProperties = {
    transform: `translate3d(calc(${-index * 100}% + ${dragPx}px), 0, 0)`,
    transition: isDragging
      ? "none"
      : "transform 300ms cubic-bezier(0.22, 1, 0.36, 1)",
  };

  return (
    <div
      ref={viewportRef}
      className="tp-carousel"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
    >
      <div className="tp-carousel-track" style={trackStyle}>
        {slides.map((child, i) => (
          <div className="tp-carousel-slide" key={i}>
            {child}
          </div>
        ))}
      </div>
    </div>
  );
});

MobileCarousel.displayName = "MobileCarousel";

export default MobileCarousel;
